import Vue from 'vue'
import {
  PrepAutoCheckOutcome,
  PrepTask,
  PrepTaskStatus,
  ProductionPrep,
  ProductionPrepArea,
  ProductionPrepAreaProgress,
  ProductionPrepAreaSummary,
  ProductionPrepSummary,
  ProductionPrepTask
} from '@/models'
import { Utils } from '@/utils'
import { ActionTypes } from '@/store/action-types'

export const PREP_TASK_STATUS: { [status in PrepTaskStatus]: status } = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  REQUESTED: 'REQUESTED',
  DONE: 'DONE',
  NOT_APPLICABLE: 'NOT_APPLICABLE'
}

/** Done or not applicable: nothing left to do with the task */
export const RESOLVED_PREP_TASK_STATUSES: PrepTaskStatus[] = [PREP_TASK_STATUS.DONE, PREP_TASK_STATUS.NOT_APPLICABLE]

export type PrepTaskStatusOption = {
  value: PrepTaskStatus;
  labelKey: string;
  icon: string;
  variant: string;
}

// Statuses of a preparation task, in the order of the status buttons. Same palette as the
// preparation states: green done, amber waiting, grey pending or not applicable; blue being worked on
export const PREP_TASK_STATUSES: PrepTaskStatusOption[] = [
  { value: PREP_TASK_STATUS.PENDING, labelKey: 'production_prep.status.PENDING', icon: 'mdi-circle-outline', variant: 'secondary' },
  { value: PREP_TASK_STATUS.IN_PROGRESS, labelKey: 'production_prep.status.IN_PROGRESS', icon: 'mdi-circle-slice-4', variant: 'primary' },
  { value: PREP_TASK_STATUS.REQUESTED, labelKey: 'production_prep.status.REQUESTED', icon: 'mdi-timer-sand', variant: 'warning' },
  { value: PREP_TASK_STATUS.DONE, labelKey: 'production_prep.status.DONE', icon: 'mdi-check-circle', variant: 'success' },
  { value: PREP_TASK_STATUS.NOT_APPLICABLE, labelKey: 'production_prep.status.NOT_APPLICABLE', icon: 'mdi-minus-circle-outline', variant: 'secondary' }
]

export const prepTaskStatusOption = (status: PrepTaskStatus): PrepTaskStatusOption =>
  PREP_TASK_STATUSES.find(option => option.value === status) || PREP_TASK_STATUSES[0]

/** Started but not finished: in progress or waiting for someone else, they show since when */
export const ONGOING_PREP_TASK_STATUSES: PrepTaskStatus[] = [PREP_TASK_STATUS.IN_PROGRESS, PREP_TASK_STATUS.REQUESTED]

/** "en curso hace 3 días", "solicitada hoy"... for the ongoing statuses */
export function ongoingSince(vm: Vue, status: PrepTaskStatus, date?: string | null): string {
  if (!date) return vm.$t(prepTaskStatusOption(status).labelKey).toString()
  const days = Utils.daysSince(date)
  return vm.$tc(`production_prep.since.${status}`, days, { count: days }).toString()
}

/** How the result of an automatic check shows: confirmed green, not confirmed amber, the rest muted */
export const PREP_AUTO_CHECK_OUTCOMES: { [outcome in PrepAutoCheckOutcome]: { icon: string; variant: string } } = {
  CONFIRMED: { icon: 'mdi-check-decagram', variant: 'success' },
  NOT_CONFIRMED: { icon: 'mdi-alert-circle-outline', variant: 'warning' },
  NOT_READY: { icon: 'mdi-help-circle-outline', variant: 'secondary' },
  ERROR: { icon: 'mdi-alert-octagon-outline', variant: 'danger' }
}

/** Name of an automatic check; one this app doesn't know yet says so */
export const autoCheckName = (vm: Vue, key: string): string =>
  vm.$te(`production_prep.auto_check.${key}.name`)
    ? vm.$t(`production_prep.auto_check.${key}.name`).toString()
    : vm.$t('production_prep.auto_check.unknown', { key }).toString()

/**
 * What the last check of the task found: its own text when it looked; when it couldn't, its own
 * reason if it has one (the brands) or the common one
 */
export function autoCheckResultText(vm: Vue, task: ProductionPrepTask): string {
  const outcome = task.autoCheckResult
  const ownKey = `production_prep.auto_check.${task.autoCheck}.${outcome}`
  if ((outcome === 'NOT_READY' || outcome === 'ERROR') && !vm.$te(ownKey)) {
    return vm.$t(`production_prep.auto_check.${outcome}`).toString()
  }
  const count = task.autoCheckCount || 0
  const total = task.autoCheckTotal
  // Plural by the total when the check has one (the brands), else by what it counted
  const choice = total === null || total === undefined ? count : total
  return vm.$tc(ownKey, choice, { count, total, detail: task.autoCheckDetail }).toString()
}

/** Preparations per page in the list, as in the accounts list */
export const PREP_LIST_PAGE_SIZE = 25

/** Placeholders of the request texts: the preparation name and the linked production project */
export const REQUEST_PLACEHOLDER_PREP = '{proyecto}'
export const REQUEST_PLACEHOLDER_PROJECT = '{proyecto_ccp}'

/** Request text with its placeholders replaced (the project one is kept as is while there is none) */
export function resolveRequestText(text: string | null | undefined, prep: ProductionPrep): string {
  const project = prep.editorialProjectName || prep.editorialProjectId
  return (text || '')
    .split(REQUEST_PLACEHOLDER_PREP).join(prep.name)
    .split(REQUEST_PLACEHOLDER_PROJECT).join(project || REQUEST_PLACEHOLDER_PROJECT)
}

/** Tasks of an area that count for its progress: the required ones, or every task when none is required */
export function countedTasks(area: ProductionPrepArea): ProductionPrepTask[] {
  return area.tasks.some(task => task.required) ? area.tasks.filter(task => task.required) : area.tasks
}

/** Every task of the preparation, area after area */
export const prepTasks = (prep: ProductionPrep): ProductionPrepTask[] =>
  prep.areas.reduce((all: ProductionPrepTask[], area) => all.concat(area.tasks), [])

/** Counted tasks of the preparation, those that give its progress */
export const prepCountedTasks = (prep: ProductionPrep): ProductionPrepTask[] =>
  prep.areas.reduce((all: ProductionPrepTask[], area) => all.concat(countedTasks(area)), [])

/** Of the counted tasks, the ones that apply (not applicable ones leave the total) and the done ones */
export function taskProgress(counted: ProductionPrepTask[]): { applicable: number; done: number } {
  return {
    applicable: counted.filter(task => task.status !== PREP_TASK_STATUS.NOT_APPLICABLE).length,
    done: counted.filter(task => task.status === PREP_TASK_STATUS.DONE).length
  }
}

/** Done out of the applicable ones (0-100); nothing to do is 100 */
export const percent = (done: number, applicable: number): number =>
  applicable ? Math.round(done * 100 / applicable) : 100

/** Required tasks done, out of the required ones that apply */
export const prepProgress = (prep: ProductionPrep): number => percent(prep.resolvedRequiredTasks, prep.requiredTasks)

/** ready: nothing left; todo: nothing done yet; progress: in between */
export const PREP_STATE = {
  READY: 'ready',
  PROGRESS: 'progress',
  TODO: 'todo'
} as const

export type PrepState = typeof PREP_STATE[keyof typeof PREP_STATE]

export const PREP_STATES: { [state in PrepState]: { icon: string; variant: string } } = {
  ready: { icon: 'mdi-check-circle', variant: 'success' },
  progress: { icon: 'mdi-progress-check', variant: 'warning' },
  todo: { icon: 'mdi-circle-outline', variant: 'secondary' }
}

export const prepState = (applicable: number, done: number): PrepState =>
  done >= applicable ? PREP_STATE.READY : done === 0 ? PREP_STATE.TODO : PREP_STATE.PROGRESS

/** "Listo", "Sin empezar" or "Faltan N" */
export const prepStateLabel = (vm: Vue, state: PrepState, missing: number): string =>
  state === PREP_STATE.READY
    ? vm.$t('production_prep.state.ready').toString()
    : state === PREP_STATE.TODO
      ? vm.$t('production_prep.state.not_started').toString()
      : vm.$t('production_prep.state.missing', { count: missing }).toString()

export interface PrepStateView {
  state: PrepState;
  icon: string;
  variant: string;
  label: string;
  missing: number;
}

/** State of a preparation with its icon, colour and text, from its applicable and done tasks */
export function prepStateView(vm: Vue, applicable: number, done: number): PrepStateView {
  const state = prepState(applicable, done)
  const missing = applicable - done
  return { state, ...PREP_STATES[state], label: prepStateLabel(vm, state, missing), missing }
}

export const AREA_PROGRESS: { [progress in ProductionPrepAreaProgress]: progress } = {
  NONE: 'NONE',
  PARTIAL: 'PARTIAL',
  DONE: 'DONE',
  NOT_APPLICABLE: 'NOT_APPLICABLE'
}

const AREA_PROGRESS_STATES: { [state in PrepState]: ProductionPrepAreaProgress } = {
  ready: AREA_PROGRESS.DONE,
  progress: AREA_PROGRESS.PARTIAL,
  todo: AREA_PROGRESS.NONE
}

/** Progress of an area: the same states as a preparation, or not applicable when nothing applies */
export const areaProgress = (applicable: number, done: number): ProductionPrepAreaProgress =>
  applicable ? AREA_PROGRESS_STATES[prepState(applicable, done)] : AREA_PROGRESS.NOT_APPLICABLE

/** Icon and colour of each area progress: those of the matching preparation state, muted when nothing applies */
export const AREA_PROGRESS_ICONS: { [progress in ProductionPrepAreaProgress]: { icon: string; variant: string } } = {
  DONE: PREP_STATES.ready,
  PARTIAL: PREP_STATES.progress,
  NONE: PREP_STATES.todo,
  NOT_APPLICABLE: { icon: prepTaskStatusOption(PREP_TASK_STATUS.NOT_APPLICABLE).icon, variant: 'muted' }
}

/** Done and not applicable areas start collapsed: nothing left to see in them */
export const isAreaOpenByDefault = (progress: ProductionPrepAreaProgress): boolean =>
  progress !== AREA_PROGRESS.DONE && progress !== AREA_PROGRESS.NOT_APPLICABLE

export interface PrepSummaryTotals {
  applicable: number;
  done: number;
  missing: number;
  requested: number;
  percent: number;
  state: PrepState;
  byStatus: { [status in PrepTaskStatus]: number };
}

/** Totals of a preparation summary; the not applicable tasks are out of the total */
export function prepSummaryTotals(prep: ProductionPrepSummary): PrepSummaryTotals {
  const applicable = prep.areas.reduce((total, area) => total + area.tasks - area.notApplicableTasks, 0)
  const done = prep.areas.reduce((total, area) => total + area.doneTasks, 0)
  const requested = prep.areas.reduce((total, area) => total + area.requestedTasks, 0)
  const inProgress = prep.areas.reduce((total, area) => total + area.inProgressTasks, 0)
  const notApplicable = prep.areas.reduce((total, area) => total + area.notApplicableTasks, 0)
  return {
    applicable,
    done,
    missing: applicable - done,
    requested,
    percent: percent(done, applicable),
    state: prepState(applicable, done),
    byStatus: {
      PENDING: applicable - done - requested - inProgress,
      IN_PROGRESS: inProgress,
      REQUESTED: requested,
      DONE: done,
      NOT_APPLICABLE: notApplicable
    }
  }
}

/** Tasks of the area summary that apply, the not applicable ones out */
export const areaApplicableTasks = (area: ProductionPrepAreaSummary): number => area.tasks - area.notApplicableTasks

/** Error toast of the store with the backend message of a failed call, or the given text */
export function showPrepError(vm: Vue, error: any, message = vm.$t('feedback.error_message_generic').toString()) {
  if (error) console.error(error)
  vm.$store.dispatch(ActionTypes.SHOW_TOAST, {
    title: vm.$t('feedback.error_title_generic').toString(),
    variant: 'danger',
    message: Utils.apiErrorMessage(error) || message
  })
}

/** Confirmation before changing something of the preparations; a removal by default */
export const confirmPrep = (
  vm: Vue,
  message: string,
  title = vm.$t('production_prep.confirm_delete_title').toString(),
  okTitle = vm.$t('button_actions.remove').toString(),
  okVariant = 'danger'
): Promise<boolean> =>
  vm.$bvModal.msgBoxConfirm(message, {
    title,
    okVariant,
    okTitle,
    cancelTitle: vm.$t('button_actions.cancel').toString(),
    centered: true
  })

/** New task of the area, as the task modal edits it */
export const newPrepTask = (areaId: number): PrepTask =>
  ({ id: 0, areaId, name: '', required: true, links: [], sortOrder: 0 })

/** Shows the modal once the item selected for it is rendered */
export const showPrepModal = (vm: Vue, modalId: string): void => {
  vm.$nextTick(() => vm.$bvModal.show(modalId))
}

/**
 * Saves what a preparation modal emits: closes the modal and goes on with the saved item, or
 * shows the error and keeps the modal open
 */
export async function savePrepItem<T>(
  vm: Vue,
  save: () => Promise<T>,
  done: (saved: boolean) => void,
  after: (saved: T) => unknown
) {
  try {
    const saved = await save()
    done(true)
    await after(saved)
  } catch (error) {
    done(false)
    showPrepError(vm, error)
  }
}

/** Asks before removing something of the preparations, then removes it and goes on */
export async function removePrepItem(vm: Vue, message: string, remove: () => Promise<unknown>, after: () => unknown) {
  if (!(await confirmPrep(vm, message))) return
  try {
    await remove()
    await after()
  } catch (error) {
    showPrepError(vm, error)
  }
}
